import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { PublicPageSeo } from "@/components/seo/PublicPageSeo";
import { buildHomeSoftwareJsonLd } from "@/seo/productSchemas";
import { useExpiredSubscriptionRedirect } from "@/hooks/useExpiredSubscriptionRedirect";
import {
  Truck, ClipboardCheck, Shield, Camera, ArrowRight,
  AlertTriangle, MessageSquare, Search, Check, PoundSterling,
  FileText, XCircle,
} from "lucide-react";
import { CompetitorComparison } from "@/components/CompetitorComparison";
import { LandingPricingTiers } from "@/components/LandingPricingTiers";
import { Section, SectionHeading } from "@/components/marketing/Section";
import { ScreenshotSplit } from "@/components/marketing/ScreenshotSplit";
import { ScreenshotFrame } from "@/components/marketing/ScreenshotFrame";
import { TestimonialCards } from "@/components/marketing/TestimonialCards";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { desktopShots, mobileShots, integrations } from "@/data/marketing";

const heroPains = [
  { icon: MessageSquare, text: "Jobs sent over WhatsApp" },
  { icon: AlertTriangle, text: "Chasing drivers for updates" },
  { icon: AlertTriangle, text: "Missing vehicle damage" },
  { icon: FileText, text: "Doing invoices on weekends" },
];

const aiBenefits = [
  { icon: Shield, label: "Stronger protection", desc: "Verified condition evidence for every job" },
  { icon: Search, label: "Clearer documentation", desc: "AI-generated condition reports with severity ratings" },
  { icon: AlertTriangle, label: "Fewer disputes", desc: "Irrefutable proof with timestamped photos and signatures" },
];

const howItWorks = [
  {
    step: 1,
    icon: ClipboardCheck,
    title: "Create the job",
    desc: "Add vehicle, pickup, delivery and driver in seconds. The driver gets it instantly on their phone.",
  },
  {
    step: 2,
    icon: Camera,
    title: "Driver completes the job in the app",
    desc: "Photos, signatures, GPS and condition reports — captured automatically at collection and delivery.",
  },
  {
    step: 3,
    icon: PoundSterling,
    title: "Invoice sent automatically",
    desc: "Proof of delivery goes to the customer. Invoice is created and synced to your accounting software.",
  },
];

const exploreLinks = [
  { to: "/features", label: "All features", desc: "AI damage detection, appraisals, tracking, invoicing and the driver app." },
  { to: "/solutions/vehicle-transport-software", label: "Vehicle transport software", desc: "The complete workflow from job and dispatch through PoC/PoD to invoicing." },
  { to: "/features/proof-of-delivery", label: "Proof of delivery software", desc: "Digital proof of collection and delivery with photos, damage, signatures and GPS." },
  { to: "/features/epod-software", label: "ePOD software", desc: "Electronic proof of delivery built around vehicle condition, not just a signature." },
  { to: "/features/vehicle-inspection", label: "Vehicle inspection app", desc: "Collection and delivery condition reports captured on the driver's phone." },
  { to: "/features/offline-mode", label: "Offline vehicle inspections", desc: "Full offline inspection capability — complete vehicle inspections without mobile signal." },
  { to: "/solutions#subcontracting", label: "Subcontracting & compliance", desc: "Subcontract jobs, audit trails and how TransportMate compares to spreadsheets." },
  { to: "/integrations", label: "Integrations", desc: "QuickBooks, Xero and FreeAgent invoice sync — plus what is coming next." },
  { to: "/resources#faqs", label: "FAQs & help centre", desc: "Answers on plans, offline mode, tracking links and adding drivers." },
];

const cardClass =
  "bg-card border rounded-2xl transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl";

const Landing = () => {
  useExpiredSubscriptionRedirect(["/", "/welcome"]);
  const seoPath = useLocation().pathname === "/welcome" ? "/welcome" : "/";
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  const trialButton = (label: string) => (
    <Button size="lg" className="w-full sm:w-auto h-12 sm:h-14 px-10 text-base sm:text-lg font-bold" onClick={scrollToPricing}>
      {label}
      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
    </Button>
  );

  return (
    <>
      <PublicPageSeo path={seoPath}>
        {seoPath === "/" ? (
          <script type="application/ld+json">
            {JSON.stringify(buildHomeSoftwareJsonLd())}
          </script>
        ) : null}
      </PublicPageSeo>

      {/* HERO */}
      <section
        ref={heroAnimation.ref as React.RefObject<HTMLElement>}
        className="relative overflow-hidden"
        style={{ background: 'var(--gradient-hero)' }}
      >
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, hsl(var(--primary)) 1px, transparent 1px), radial-gradient(circle at 75% 75%, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }} />

        <div className={`container mx-auto px-4 pt-10 pb-12 sm:py-24 text-center relative z-10 transition-[opacity,transform] duration-700 motion-reduce:transition-none ${
          heroAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          <p className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-5 sm:mb-6 border border-primary/10">
            <Truck className="h-4 w-4" aria-hidden="true" />
            All-in-One Transport Operating System
          </p>

          <h1 className="text-2xl sm:text-4xl md:text-[2.75rem] md:leading-[1.1] font-extrabold tracking-tight mb-4 sm:mb-5 max-w-3xl mx-auto">
            Stop running your transport jobs through <span className="text-primary">WhatsApp.</span>
          </h1>

          <p className="text-sm sm:text-lg text-muted-foreground leading-relaxed mb-6 sm:mb-8 max-w-2xl mx-auto">
            Track drivers, capture vehicle damage, and send invoices automatically — all from one system.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-7 sm:mb-9 text-left">
            {heroPains.map((item) => (
              <div key={item.text} className="flex gap-2.5 items-center bg-card/70 border rounded-xl px-3.5 py-3">
                <span className="w-7 h-7 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <XCircle className="h-3.5 w-3.5 text-destructive/70" aria-hidden="true" />
                </span>
                <span className="text-sm font-medium text-foreground">{item.text}</span>
              </div>
            ))}
          </div>

          {isAuthenticated ? (
            <Link to="/dashboard">
              <Button size="lg" className="h-12 px-8 text-base font-bold">Go to Dashboard</Button>
            </Link>
          ) : (
            trialButton("Start Your 30-Day Free Trial")
          )}

          <p className="mt-3 text-xs sm:text-sm text-muted-foreground font-medium">
            No contracts • Cancel anytime • No charge until trial ends
          </p>
          <p className="mt-2 text-xs sm:text-sm font-semibold text-primary">
            Built for UK vehicle transport businesses — from owner drivers to multi-driver fleets
          </p>

          <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" aria-hidden="true" /> GPS-stamped proof</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" aria-hidden="true" /> AI damage detection</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" aria-hidden="true" /> Auto invoicing</span>
          </div>
        </div>
      </section>

      {/* BUSINESSES OF EVERY SIZE */}
      <Section>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Vehicle transport software for businesses of every size
          </h2>
          <p className="mt-4 text-sm sm:text-lg text-muted-foreground leading-relaxed">
            Whether you're a one-person vehicle transporter managing everything yourself or a growing
            transport company with multiple drivers, TransportMate brings your quotes, jobs, customers,
            vehicle inspections, tracking, PODs and invoicing together.
          </p>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            From single-vehicle trade plate movements to multi-vehicle transport operations, TransportMate
            keeps jobs, drivers, vehicle condition evidence, customer updates, proof of delivery and
            invoicing connected. You don't need a large transport company to benefit. Collections, inspections, photos,
            signatures and proof of delivery are completed in the TransportMate app on your phone.
            See{" "}
            <Link to="/vehicle-transport-software-for-small-businesses" className="text-primary font-semibold hover:underline">
              vehicle transport software for owner drivers and small businesses
            </Link>
            ,{" "}
            <Link to="/trade-plate-driver-software" className="text-primary font-semibold hover:underline">
              software for trade plate drivers
            </Link>
            , or the full{" "}
            <Link to="/solutions/vehicle-transport-software" className="text-primary font-semibold hover:underline">
              vehicle transport management software
            </Link>{" "}
            overview.
          </p>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <SectionHeading
          eyebrow="How it works"
          title="How TransportMate Works"
          description="From job creation to payment — everything happens in one simple workflow."
        />
        <div className="grid gap-5 sm:grid-cols-3 max-w-5xl mx-auto">
          {howItWorks.map((item) => (
            <div key={item.step} className={`${cardClass} relative p-6 text-center`} style={{ boxShadow: "var(--shadow-card)" }}>
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold">
                {item.step}
              </span>
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4 mt-2">
                <item.icon className="h-6 w-6 text-primary" aria-hidden="true" />
              </span>
              <h3 className="font-bold text-base mb-1.5">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        {!isAuthenticated && (
          <div className="text-center mt-10">
            {trialButton("Start Your 30-Day Free Trial")}
            <p className="mt-3 text-xs text-muted-foreground">No contracts • Cancel anytime • No charge until trial ends</p>
          </div>
        )}
      </Section>

      {/* AI DAMAGE DETECTION */}
      <Section tone="alt">
        <SectionHeading
          eyebrow="Industry First"
          title="Built-In AI Damage Detection & Delivery Proof"
          description={
            <>
              Capture photos at collection and delivery and let TransportMate automatically detect vehicle damage using AI. Every report includes verified time, GPS location, and customer signatures so you always have <strong className="text-foreground">clear proof of vehicle condition</strong>.
            </>
          }
        />
        <div className="grid gap-8 lg:grid-cols-2 items-center max-w-6xl mx-auto">
          <ScreenshotFrame src={mobileShots[1].image} alt="AI damage scan on the TransportMate driver app" variant="device" />
          <div className="grid gap-4">
            {aiBenefits.map((b) => (
              <div key={b.label} className={`${cardClass} p-5 flex items-start gap-4`} style={{ boxShadow: "var(--shadow-card)" }}>
                <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <b.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-bold text-sm mb-0.5">{b.label}</span>
                  <span className="block text-sm text-muted-foreground leading-relaxed">{b.desc}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* PRODUCT PROOF */}
      <Section>
        <SectionHeading
          eyebrow="Inside the platform"
          title="Powerful Desktop Management"
          description="Run your entire transport operation from one command centre — built for office admins and dispatchers. Click the screenshot to view it full size."
        />
        <div className="max-w-6xl mx-auto">
          <ScreenshotSplit shot={desktopShots[0]} index={0} />
        </div>
        <div className="text-center mt-10">
          <Link to="/features">
            <Button variant="outline" size="lg" className="h-12 px-8 font-semibold">
              See every screen and feature
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* TRUST STRIP */}
      <Section tone="alt" className="!py-10">
        <p className="text-center text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-primary mb-5">
          Works with your existing workflow
        </p>
        <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8">
          {integrations.map((i) => (
            <span key={i.name} className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-md flex items-center justify-center bg-white border border-border/60 p-1">
                <img
                  src={i.logo}
                  alt={`${i.name} logo`}
                  width={24}
                  height={24}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain"
                />
              </span>
              <span className="font-semibold text-sm">{i.name}</span>
            </span>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4 font-medium">
          Built specifically for UK vehicle transport professionals
        </p>
      </Section>

      {/* TESTIMONIALS */}
      <Section>
        <SectionHeading
          eyebrow="Real operators. Real results."
          title="Trusted by Transport Professionals"
        />
        <TestimonialCards />
      </Section>

      {/* COMPETITOR COMPARISON */}
      <CompetitorComparison />

      {/* PRICING */}
      <Section id="pricing">
        <SectionHeading
          title="Choose Your Plan"
          description="Every plan includes a 30-day free trial"
        />
        <LandingPricingTiers />
      </Section>

      {/* FINAL CTA */}
      <Section tone="alt">
        <div className="bg-primary rounded-3xl p-6 sm:p-12 text-center text-primary-foreground relative overflow-hidden">
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }} />
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-4">
              Stop running jobs through WhatsApp.<br className="hidden sm:block" /> Start running them through TransportMate.
            </h2>
            <p className="text-sm sm:text-lg mb-7 text-primary-foreground/85 max-w-xl mx-auto leading-relaxed">
              Built for UK vehicle transport companies. 30 days free. No charge until your trial ends.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="w-full sm:w-auto h-12 sm:h-14 px-10 text-base sm:text-lg font-bold"
              onClick={scrollToPricing}
            >
              Start Your 30-Day Free Trial
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
            <p className="mt-3 text-xs sm:text-sm text-primary-foreground/80 font-medium">
              No contracts • Cancel anytime • No charge until trial ends
            </p>
          </div>
        </div>
      </Section>

      {/* EXPLORE MORE */}
      <Section>
        <SectionHeading
          title="Explore TransportMate in detail"
          description="Deeper walkthroughs of the features, workflows, integrations and answers operators ask for most."
        />
        <div className="grid gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
          {exploreLinks.map((l) => (
            <Link key={l.to} to={l.to} className={`${cardClass} p-6 block group`} style={{ boxShadow: "var(--shadow-card)" }}>
              <h3 className="font-bold text-base mb-1.5">{l.label}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{l.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Open
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8">
          Have questions?{" "}
          <Link to="/contact" className="font-semibold text-primary underline underline-offset-4">
            Get in touch with the team
          </Link>
          .
        </p>
      </Section>
    </>
  );
};

export default Landing;
